// render-games.ts — the "catalog-games" renderer: the game-listing pages (free_games.php / paid_games.php).
//
// Same JSON pipeline as the cards/table/rules (PHP selects raw campaign rows;
// the browser holds it in memory and filters by keyword client-side, no jQuery) — but the card is the
// bespoke campaign-listing card: header image, title, level/type/mic/webcam/price badges, the next
// session number + date, and seats-filled. Cloned element-for-element from the legacy
// options/table_lists/table_list_free_games_inc.php so the card is visually identical.
//
// The toolbar search box (data-ft-search-input-id) filters cards by name, instantly and client-side —
// the old page did a debounced POST per keystroke that re-ran the SQL; that's gone.

import type { CatalogGamesResponse, CatalogListConfig, GameCard } from './types';
import { campaignGameCards, requireCampaignList, parseCampaignRowsResponse } from '../shared/campaign-list-rows';
import { resolveUrl, results, setStatus, showLoader, hideLoader } from './catalog-list';
import { buildEmptyState, emptyStateFromAttributes } from '../shared/empty-state';
import { wireFilterClearButton } from './filter-clear';

// One game-listing card: link → card → header image → body title → list-group of badges/info. Built
// with the DOM API (createElement + textContent) so the campaign name is escaped intrinsically; only
// the fixed browser icon HTML (calendar/mic/webcam) is injected as
// trusted HTML — same contract as the rules cards' icon_html.
function renderGameCard(card: GameCard, icons: CatalogGamesResponse['icons']): HTMLAnchorElement {
    const link = document.createElement('a');
    link.href = card.url;
    link.className = 'text-decoration-none';
    link.dataset.ftCampaignId = card.item_id;

    const cardEl = document.createElement('div');
    cardEl.className = 'card text-left mb-3';

    const img = document.createElement('img');
    img.src = card.picture_url || '';
    img.className = 'card-img-top';
    img.alt = `${card.name || 'Campaign'} Header`;
    img.loading = 'lazy';
    cardEl.appendChild(img);

    const body = document.createElement('div');
    body.className = 'card-body';
    const title = document.createElement('h5');
    title.className = 'card-title';
    title.textContent = card.name || 'Untitled';
    body.appendChild(title);
    cardEl.appendChild(body);

    const list = document.createElement('ul');
    list.className = 'list-group list-group-flush';

    // ── Row 1: level + type badges (left), mic/webcam/price badges (right) ─────────────────────────
    const li1 = document.createElement('li');
    li1.className = 'list-group-item';
    const row = document.createElement('div');
    row.className = 'd-flex align-items-center';

    const left = document.createElement('div');
    const h6 = document.createElement('h6');
    h6.className = 'm-0';
    const levelBadge = document.createElement('span');
    levelBadge.className = 'badge bg-primary';
    levelBadge.dataset.ftCampaignMetric = 'level';
    levelBadge.textContent = `Level ${card.level}`;
    h6.appendChild(levelBadge);
    h6.appendChild(document.createTextNode(' '));
    const typeBadge = document.createElement('span');
    // type_badge_class is a browser-built suffix from a fixed switch (never user input), e.g.
    // "bg-secondary" / "bg-success" / "bg-warning text-dark".
    typeBadge.className = `badge ${card.type_badge_class || 'bg-secondary'}`;
    typeBadge.dataset.ftCampaignMetric = 'type';
    typeBadge.textContent = card.type_label;
    h6.appendChild(typeBadge);
    left.appendChild(h6);
    row.appendChild(left);

    const right = document.createElement('div');
    right.className = 'ms-auto';
    if (card.mic_required) {
        const b = document.createElement('span');
        b.className = 'badge bg-warning';
        b.innerHTML = icons.microphone_required;
        right.appendChild(b);
        right.appendChild(document.createTextNode(' '));
    }
    if (card.webcam_required) {
        const b = document.createElement('span');
        b.className = 'badge bg-warning';
        b.innerHTML = icons.webcam_required;
        right.appendChild(b);
        right.appendChild(document.createTextNode(' '));
    }
    if (card.price_f) {
        const b = document.createElement('span');
        b.className = 'badge bg-success';
        b.textContent = card.price_f;
        right.appendChild(b);
    }
    row.appendChild(right);
    li1.appendChild(row);
    list.appendChild(li1);

    // ── Row 2: next session number | calendar + date ──────────────────────────────────────────────
    const li2 = document.createElement('li');
    li2.className = 'list-group-item';
    const numBadge = document.createElement('span');
    numBadge.className = 'badge bg-info';
    numBadge.dataset.ftCampaignMetric = 'next_session_number';
    numBadge.textContent = card.next_session_num;
    li2.appendChild(numBadge);
    li2.appendChild(document.createTextNode(' | '));
    const cal = document.createElement('span');
    cal.innerHTML = icons.calendar;
    li2.appendChild(cal);
    const date = document.createElement('span');
    date.dataset.ftCampaignMetric = 'next_session_date';
    date.textContent = ' ' + card.next_session_date;
    li2.appendChild(date);
    list.appendChild(li2);

    // ── Row 3: seats filled ───────────────────────────────────────────────────────────────────────
    const li3 = document.createElement('li');
    li3.className = 'list-group-item';
    const filled = document.createElement('span');
    filled.className = 'badge bg-warning text-dark';
    filled.dataset.ftCampaignMetric = 'seats_filled';
    filled.textContent = card.seats_filled;
    li3.appendChild(filled);
    li3.appendChild(document.createTextNode(' of '));
    const max = document.createElement('span');
    max.className = 'badge bg-info text-dark';
    max.dataset.ftCampaignMetric = 'seats_max';
    max.textContent = card.seats_max;
    li3.appendChild(max);
    li3.appendChild(document.createTextNode(' Seats Filled'));
    list.appendChild(li3);

    cardEl.appendChild(list);
    link.appendChild(cardEl);
    return link;
}

interface BuiltCard {
    el: HTMLElement;
    text: string;
    price: number;
    level: number;
    seatsFilled: number;
    seatsMax: number;
    mic: boolean;
    webcam: boolean;
}

// Read a numeric filter control by element id; '' / missing / NaN → null (no bound).
function readNumField(elementId: string | undefined): number | null {
    if (!elementId) return null;
    const el = document.getElementById(elementId) as HTMLInputElement | null;
    const v = (el?.value ?? '').trim();
    if (v === '') return null;
    const n = parseFloat(v);
    return Number.isNaN(n) ? null : n;
}

// Read a checkbox filter control by element id; missing → false.
function readCheckField(elementId: string | undefined): boolean {
    if (!elementId) return false;
    const el = document.getElementById(elementId) as HTMLInputElement | null;
    return !!el?.checked;
}

// Close the filter panel (slideToggleEl if available, else hide) — same contract as the cards/table.
function closePanel(panelId: string | null): void {
    if (!panelId) return;
    if (typeof window.slideToggleEl === 'function') {
        window.slideToggleEl(panelId);
        return;
    }
    const panel = document.getElementById(panelId);
    if (panel) panel.style.display = 'none';
}

export function mountCatalogGames(root: HTMLElement, config: CatalogListConfig): void {
    const container = results(root);
    if (!container) {
        console.error('[catalog-games] mount missing [data-ft-catalog-results]', root);
        return;
    }

    const searchInput = config.searchInputId
        ? (document.getElementById(config.searchInputId) as HTMLInputElement | null)
        : null;

    void (async () => {
        const loadingTimer = setTimeout(() => showLoader(root, config), 250);
        try {
            const res = await fetch(resolveUrl(config.endpoint), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: '{}',
            });
            // Read as text first so a stray PHP warning / HTML 500 surfaces a useful message rather
            // than an opaque JSON.parse error (same guard as render-rules.ts / render-table.ts).
            const raw = await res.text();
            const payload = requireCampaignList(parseCampaignRowsResponse(raw, res.status, 'campaign-list-v1'));
            const data = { games: campaignGameCards(payload, resolveUrl('')), total: payload.total,
                icons: { calendar: iconMap('calendar', 20, 20), microphone_required: iconMap('microphone_required', 20, 20), webcam_required: iconMap('webcam_required', 20, 20) } };
            clearTimeout(loadingTimer);
            hideLoader(root);
            setStatus(root, '');
            build(root, container, config, data, searchInput);
        } catch (e) {
            clearTimeout(loadingTimer);
            hideLoader(root);
            console.error('[catalog-games] fetch/render failed', e);
            setStatus(root, '');
            container.innerHTML = '';
            const msg = document.createElement('div');
            msg.className = 'col-12 text-center text-muted py-4';
            msg.textContent = e instanceof Error ? e.message : 'Could not load games. Please try again.';
            container.appendChild(msg);
        }
    })();
}

function build(
    root: HTMLElement,
    container: HTMLElement,
    config: CatalogListConfig,
    data: CatalogGamesResponse,
    searchInput: HTMLInputElement | null,
): void {
    container.innerHTML = '';

    const icons = data.icons || { calendar: '', microphone_required: '', webcam_required: '' };

    // The card <a> is the DIRECT grid child (no .col wrapper) — matching the legacy free_games.php
    // markup and the rules renderer. css/style.css ships its own copy of Bootstrap's `.col` AFTER the
    // CDN but WITHOUT the `.row-cols-*` rules, so a `.col` wrapper would collapse every card to one
    // line; `.row-cols-lg-3 > a` has no such override.
    const built: BuiltCard[] = (data.games || []).map((card) => {
        const el = renderGameCard(card, icons);
        container.appendChild(el);
        return {
            el,
            text: card.text || (card.name || '').toLowerCase(),
            price: Number(card.price) || 0,
            level: Number(card.level_num) || 0,
            seatsFilled: parseInt(card.seats_filled, 10) || 0,
            seatsMax: parseInt(card.seats_max, 10) || 0,
            mic: !!card.mic_required,
            webcam: !!card.webcam_required,
        };
    });

    // Empty state for no loaded games or filters that hide every card.
    const emptyEl = document.createElement('div');
    emptyEl.className = 'col-12 text-center text-muted py-5';
    emptyEl.textContent = 'No games match your search.';
    emptyEl.style.display = 'none';
    container.appendChild(emptyEl);
    const emptySpec = built.length === 0 ? emptyStateFromAttributes(root, 'No games match your search.') : null;
    if (emptySpec) {
        emptyEl.className = 'col-12';
        emptyEl.replaceChildren(buildEmptyState(emptySpec));
    }

    // Hydrate <i class="ft-icon"> placeholders (icon_ph) the card icons carry. This renderer doesn't
    // call initTooltips(), so mount icons explicitly (idempotent; footer/template delegation wires hover).
    if (typeof window.ftMountIcons === 'function') {
        try { window.ftMountIcons(container); } catch { /* footer pass covers init */ }
    }

    // Optional filter panel. The field ids come from the mount's data-ft-filter-fields; every branch is
    // guarded by whether its control exists, so a page that omits a control simply skips that filter:
    //   price_min/price_max   — paid_games only (number range on the session price)
    //   level_min/level_max   — number range on the campaign level
    //   hide_full             — checkbox: drop games at max seats
    //   mic_required          — checkbox: keep only games requiring a microphone
    //   webcam_required       — checkbox: keep only games requiring a webcam
    const ff = config.filterFields || {};

    const applyFilter = (): void => {
        const keyword = (searchInput?.value || '').trim().toLowerCase();
        const priceMin = readNumField(ff.price_min);
        const priceMax = readNumField(ff.price_max);
        const levelMin = readNumField(ff.level_min);
        const levelMax = readNumField(ff.level_max);
        const hideFull = readCheckField(ff.hide_full);
        const micOnly = readCheckField(ff.mic_required);
        const webcamOnly = readCheckField(ff.webcam_required);
        let visible = 0;
        for (const c of built) {
            const isFull = c.seatsMax > 0 && c.seatsFilled >= c.seatsMax;
            const show = (!keyword || c.text.includes(keyword))
                && (priceMin === null || c.price >= priceMin)
                && (priceMax === null || c.price <= priceMax)
                && (levelMin === null || c.level >= levelMin)
                && (levelMax === null || c.level <= levelMax)
                && (!hideFull || !isFull)
                && (!micOnly || c.mic)
                && (!webcamOnly || c.webcam);
            c.el.style.display = show ? '' : 'none';
            if (show) visible++;
        }
        emptyEl.style.display = visible === 0 ? '' : 'none';
    };

    // Filter panel: the Apply button (data-ft-filter-button-id) closes the panel + re-filters; the
    // controls also filter live (number inputs on 'input', checkboxes on 'change'). Guarded so
    // search-only pages are unaffected.
    if (config.filterButtonId) {
        const btn = document.getElementById(config.filterButtonId);
        btn?.addEventListener('click', () => { closePanel(config.filterPanelId); applyFilter(); });
    }
    wireFilterClearButton(config, applyFilter); // footer "Clear all" (panel stays open)
    for (const id of Object.values(ff)) {
        if (!id) continue;
        const el = document.getElementById(id);
        if (!el) continue;
        el.addEventListener('input', applyFilter);
        el.addEventListener('change', applyFilter);
    }

    // Live keyword search + the house spyglass/clear trailing icons (same wiring as the cards/table/rules).
    if (searchInput) {
        const box = searchInput.closest('.ft-inline-search-root');
        const glyph = box?.querySelector('[data-ft-inline-search-role="glyph"]') ?? null;
        const clear = box?.querySelector('[data-ft-inline-search-role="clear"]') ?? null;
        const syncTrailing = (): void => {
            if (!glyph || !clear) return;
            const hasText = (searchInput.value || '').trim().length > 0;
            glyph.classList.toggle('d-none', hasText);
            clear.classList.toggle('d-none', !hasText);
        };
        searchInput.addEventListener('input', () => { syncTrailing(); applyFilter(); });
        if (clear) {
            clear.addEventListener('click', (e) => {
                e.preventDefault();
                searchInput.value = '';
                searchInput.focus();
                syncTrailing();
                applyFilter();
            });
        }
        syncTrailing();
    }

    applyFilter();
}
